import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, UserX, MapPin, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flote/ui';
import type { DriverStatus } from '@flote/types/delivery';
import type { CreateDriverRequest, UpdateDriverRequest } from '@flote/contracts/http';
import { deliveryTsr } from '../../api';
import { DriverFormModal } from '../../components/drivers';
import { getErrorMessage } from '../../lib/error-utils';

function DriverStatusCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_BADGES: Record<DriverStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  off_duty: { label: 'Off Duty', variant: 'outline', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  idle: { label: 'Available', variant: 'default', className: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100' },
  active: { label: 'Busy', variant: 'default', className: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100' },
};

export function DriversPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dutyFilter, setDutyFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch dashboard stats for driver counts
  const { data: statsResponse, isLoading: statsLoading } = deliveryTsr.adminDashboard.getStats.useQuery({
    queryKey: ['dashboard-stats'],
  });
  const stats = statsResponse?.status === 200 ? statsResponse.body : null;

  // Fetch drivers list with filters
  const { data: driversResponse, isLoading: driversLoading, refetch: refetchDrivers } = deliveryTsr.adminDrivers.list.useQuery({
    queryKey: ['drivers-list', statusFilter, dutyFilter],
    queryData: {
      query: {
        status: statusFilter !== 'all' ? (statusFilter as DriverStatus) : undefined,
        onDuty: dutyFilter !== 'all' ? dutyFilter === 'on_duty' : undefined,
        limit: 50,
      },
    },
  });
  const drivers = driversResponse?.status === 200 ? driversResponse.body : [];

  // Fetch teams for the create form
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch vehicles for the create form
  const { data: vehiclesResponse } = deliveryTsr.adminVehicles.list.useQuery({
    queryKey: ['vehicles-list-for-drivers'],
    queryData: {
      query: {
        limit: 100,
      },
    },
  });
  const vehicles = vehiclesResponse?.status === 200 ? vehiclesResponse.body : [];

  // Mutation for creating drivers
  const createDriverMutation = deliveryTsr.adminDrivers.create.useMutation();

  const handleCreateDriver = useCallback(async (data: CreateDriverRequest) => {
    setIsCreating(true);
    try {
      const response = await createDriverMutation.mutateAsync({
        body: data,
      });

      if (response.status === 201) {
        toast.success('Driver created successfully');
        setIsCreateModalOpen(false);
        refetchDrivers();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to create driver');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create driver'));
      console.error('Create driver error:', error);
    } finally {
      setIsCreating(false);
    }
  }, [createDriverMutation, refetchDrivers]);

  // Compute status counts from driver list (since we don't have per-status counts in dashboard)
  const activeCount = drivers.filter(d => d.status === 'active').length;
  const idleCount = drivers.filter(d => d.status === 'idle').length;
  const offDutyCount = drivers.filter(d => d.status === 'off_duty').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Drivers</h1>
          <p className="text-muted-foreground">
            Manage and monitor delivery drivers
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Driver
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DriverStatusCard
          title="Total Drivers"
          count={stats?.totalDrivers ?? drivers.length}
          icon={Users}
          color="bg-slate-100 text-slate-600"
          isLoading={statsLoading && driversLoading}
        />
        <DriverStatusCard
          title="Busy"
          count={activeCount}
          icon={UserCheck}
          color="bg-amber-100 text-amber-600"
          isLoading={driversLoading}
        />
        <DriverStatusCard
          title="Available"
          count={idleCount}
          icon={MapPin}
          color="bg-green-100 text-green-600"
          isLoading={driversLoading}
        />
        <DriverStatusCard
          title="Off Duty"
          count={offDutyCount}
          icon={UserX}
          color="bg-slate-100 text-slate-400"
          isLoading={driversLoading}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Busy</SelectItem>
            <SelectItem value="idle">Available</SelectItem>
            <SelectItem value="off_duty">Off Duty</SelectItem>
          </SelectContent>
        </Select>

        <Select value={dutyFilter} onValueChange={setDutyFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Duty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="on_duty">On Duty</SelectItem>
            <SelectItem value="off_duty">Off Duty</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Drivers List */}
      <Card>
        <CardHeader>
          <CardTitle>Driver Roster</CardTitle>
        </CardHeader>
        <CardContent>
          {driversLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No drivers found</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                {statusFilter !== 'all' || dutyFilter !== 'all'
                  ? 'No drivers match the current filters. Try adjusting your filters.'
                  : 'No drivers have been synced from Onfleet yet. Drivers will appear here once they are active in your Onfleet organization.'}
              </p>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden xs:table-cell">On Duty</TableHead>
                      <TableHead className="hidden lg:table-cell">Vehicle</TableHead>
                      <TableHead className="hidden md:table-cell">Last Seen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drivers.map((driver) => {
                      const statusBadge = STATUS_BADGES[driver.status];
                      return (
                        <TableRow
                          key={driver.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/drivers/${driver.id}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2 sm:gap-3">
                              {driver.photoUrl ? (
                                <img
                                  src={driver.photoUrl}
                                  alt={driver.name}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-muted sm:flex">
                                  <Users className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{driver.name}</p>
                                {driver.displayName && driver.displayName !== driver.name && (
                                  <p className="text-xs text-muted-foreground">{driver.displayName}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground sm:table-cell">{driver.phone}</TableCell>
                          <TableCell>
                            <Badge variant={statusBadge.variant} className={statusBadge.className}>{statusBadge.label}</Badge>
                          </TableCell>
                          <TableCell className="hidden xs:table-cell">
                            <Badge
                              variant={driver.onDuty ? 'default' : 'outline'}
                              className={driver.onDuty ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100' : 'bg-slate-100 text-slate-500 border-slate-200'}
                            >
                              {driver.onDuty ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground lg:table-cell">
                            {driver.vehicle?.description || driver.vehicle?.type || '—'}
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground md:table-cell">
                            {driver.lastSeenAt
                              ? new Date(driver.lastSeenAt).toLocaleString()
                              : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Driver Modal */}
      <DriverFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreateDriver as (data: CreateDriverRequest | UpdateDriverRequest) => Promise<void>}
        isProcessing={isCreating}
        teams={teams}
        vehicles={vehicles}
      />
    </div>
  );
}
